import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Lock, Unlock, CheckCircle, CreditCard, Gift, Sparkles, 
  TrendingUp, Building2, Calendar, FileText, Loader2, Crown
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaywallModal({ 
  open, 
  onClose, 
  paywallCount = 0, 
  category = "matches",
  onSubscribe,
  onPromoApplied 
}) {
  const [loading, setLoading] = useState(false);
  const [showPromo, setShowPromo] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [creditsSummary, setCreditsSummary] = useState(null);

  // Fetch credits summary when modal opens
  const fetchCreditsSummary = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/credits/summary`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setCreditsSummary(data);
      }
    } catch (error) {
      console.error("Failed to fetch credits:", error);
    }
  };

  // Load credits on mount
  useState(() => {
    if (open) fetchCreditsSummary();
  }, [open]);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscription/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
        onSubscribe?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start checkout");
      }
    } catch (error) {
      toast.error("Failed to start checkout");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }

    setApplyingPromo(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscription/promo-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code: promoCode })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        onPromoApplied?.();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Invalid promo code");
      }
    } catch (error) {
      toast.error("Failed to apply promo code");
    } finally {
      setApplyingPromo(false);
    }
  };

  const creditsToDiscount = creditsSummary?.credits_to_discount || 50;
  const validatedCredits = creditsSummary?.validated_credits || 0;
  const discountUnlocked = creditsSummary?.discount_unlocked || false;
  const currentPrice = discountUnlocked ? "£39.50" : "£79";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Crown className="w-6 h-6 text-amber-500" />
            Unlock Full Access
          </DialogTitle>
          <DialogDescription>
            You've seen the top 3 {category}. Subscribe to unlock {paywallCount}+ more matches.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* What you get */}
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-5 space-y-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Unlock className="w-5 h-5 text-teal-600" />
              What you'll unlock
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-teal-600" />
                <span>All Investor Matches</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-indigo-600" />
                <span>All Pharma Matches</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <FileText className="w-4 h-4 text-purple-600" />
                <span>All Grant Opportunities</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span>All Event Recommendations</span>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="border-2 border-teal-200 rounded-xl p-5 bg-teal-50/50">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-semibold text-slate-900 text-lg">Operon Premium</div>
                <div className="text-sm text-slate-600">Full platform access</div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-teal-700">{currentPrice}</div>
                <div className="text-sm text-slate-500">/month</div>
              </div>
            </div>

            {!discountUnlocked && (
              <div className="bg-white rounded-lg p-3 mt-3">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-slate-600">Contribute intel to unlock £39.50/mo</span>
                  <span className="font-medium text-teal-600">{validatedCredits}/50 credits</span>
                </div>
                <Progress value={(validatedCredits / 50) * 100} className="h-2" />
                <p className="text-xs text-slate-500 mt-2">
                  Earn credits by sharing investor, pharma, event, or grant intelligence
                </p>
              </div>
            )}

            {discountUnlocked && (
              <Badge className="bg-green-100 text-green-700 mt-2">
                <Sparkles className="w-3 h-3 mr-1" />
                Contributor Discount Unlocked!
              </Badge>
            )}
          </div>

          {/* Subscribe Button */}
          <Button 
            onClick={handleSubscribe} 
            disabled={loading}
            className="w-full bg-teal-600 hover:bg-teal-700 h-12 text-lg"
            data-testid="subscribe-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                Subscribe Now — {currentPrice}/month
              </>
            )}
          </Button>

          {/* Promo Code Section */}
          <div className="text-center">
            {!showPromo ? (
              <button 
                onClick={() => setShowPromo(true)}
                className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1 mx-auto"
              >
                <Gift className="w-4 h-4" />
                Have a promo code? (SBC members)
              </button>
            ) : (
              <div className="flex gap-2">
                <Input 
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  className="flex-1"
                />
                <Button 
                  onClick={handleApplyPromo}
                  disabled={applyingPromo}
                  variant="outline"
                >
                  {applyingPromo ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Apply"
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
